import { useEffect, useState } from "react";
import {
  EyeOutlined,
  FilterOutlined,
  FilterFilled,
  EyeInvisibleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { Button, Space, Tooltip, Select, Dropdown } from "antd";
import TableComponent from "../utils/TableComponent";
import { employee, resource } from "../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import Search from "antd/es/transfer/search";
import { employeeId } from "../utils/userDetails";
import ResourceEditModal from "./ResourceEditModal";

const options = [
  { value: "SPAN-BLR-144", label: "ANIL KUMAR V" },
  { value: "SPAN-BLR-0001", label: "BHAGIRATH CHOUDHARY" },
  { value: "SPAN-BLR-1451", label: "PALLAV MAHESHWARI" },
  { value: "SPAN-BLR-2457", label: "RAJESH DOMMARAJU" },
  { value: "SPAN-BLR-191", label: "RAVI REDDY" },
  { value: "SPAN-BLR-033", label: "UMESH BALMRAM" },
];

function ResourceTable() {
  const [loading, setLoading] = useState(false);
  const [resourceTableData, setResourceTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [skillsData, setSkillsData] = useState([]);
  const [selectSkill, setSelectSkill] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [selectProject, setProjectSkill] = useState([]);
  const [selectTeam, setSelectTeam] = useState("");
  const [buId, setBuId] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [tableParams, setTableParams] = useState({
    page: 1,
    sortField: null,
    sortOrder: null,
    filterField: null,
    search: "",
    skills: [],
    projects: [],
  });

  const updateURLParams = (params) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);

      Object.keys(params).forEach((key) => {
        if (params[key]) {
          newParams.set(key, params[key]);
        } else {
          newParams.delete(key);
        }
      });

      return newParams;
    });
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    const skillsFromURL = searchParams.get("skills")?.split(",") || [];
    const projectsFromURL = searchParams.get("projects")?.split(",") || [];
    const teamFromURL = searchParams.get("team") || "";
    const globalSearchFromURL = searchParams.get("globalSearch") || "";
    const pageFromURL = searchParams.get("page") || 1;
    const sortFieldFromURL = searchParams.get("sortField") || null;
    const sortOrderFromURL = searchParams.get("sortOrder") || null;
    const filterFieldFromURL = searchParams.get("filterField") || null;
    const searchFromURL = searchParams.get("search") || "";

    setSelectSkill(skillsFromURL);
    setProjectSkill(projectsFromURL);
    setSelectTeam(teamFromURL);
    setGlobalSearch(globalSearchFromURL);

    setTableParams((prev) => ({
      page: pageFromURL,
      sortField: sortFieldFromURL,
      sortOrder: sortOrderFromURL,
      filterField: filterFieldFromURL,
      search: searchFromURL,
      skills: skillsFromURL,
      projects: projectsFromURL,
    }));
  }, []);

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "name",
      fixed: "left",
      key: "1",
      width: 180,
      change: true,
    },
    {
      title: "Employee No",
      dataIndex: "employeeNo",
      key: "2",
      width: 130,
      change: true,
    },
    {
      title: "Hiring Location",
      dataIndex: "location",
      key: "3",
      width: 160,
      change: true,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "4",
      width: 130,
      change: true,
    },
    {
      title: "Billable",
      dataIndex: "isBillable",
      key: "5",
      width: 70,
    },
    {
      title: "Availability",
      dataIndex: "available",
      key: "6",
      width: 130,
      change: true,
    },
    {
      title: "Team",
      dataIndex: "team",
      key: "7",
      width: 140,
      change: true,
    },
    {
      title: "Date of Joining",
      dataIndex: "dateOfJoin",
      key: "8",
      width: 150,
      change: true,
    },
    {
      title: "Skills",
      dataIndex: "skills",
      key: "9",
      width: 250,
      change: true,
    },
    {
      title: "Projects",
      dataIndex: "projects",
      key: "10",
      width: 250,
      change: true,
    },
    {
      title: "Reporting To",
      dataIndex: "managerName",
      key: "11",
      width: 140,
      change: true,
    },
    {
      title: "At Client",
      dataIndex: "atClient",
      key: "12",
      width: 110,
      change: true,
    },
    {
      title: "Working Location",
      dataIndex: "workingLocation",
      key: "13",
      width: 170,
      change: true,
    },
    {
      title: "Preferred Location",
      dataIndex: "preferredLocation",
      key: "14",
      width: 250,
      change: true,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "15",
      width: 500,
      change: true,
    },
    {
      title: "Resume",
      dataIndex: "resume",
      key: "16",
      width: 110,
      render: (text, record) => {
        return text ? (
          <span
            style={{
              textDecoration: "none",
              color: "#1890ff",
              cursor: "pointer",
            }}
            onClick={() =>
              navigate(`/resourceDetails`, {
                state: { employeeNo: record.employeeNo, key: "2" },
              })
            }
          >
            View Resume
          </span>
        ) : (
          ""
        );
      },
    },
    {
      title: "Available After",
      dataIndex: "availableAfter",
      key: "17",
      width: 150,
      change: true,
    },
    {
      title: "Action",
      dataIndex: "action",
      fixed: "right",
      key: "18",
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Resource Details" color="8e8e8e" key={record}>
            <Button
              type="link"
              onClick={() =>
                navigate(`/resourceDetails`, {
                  state: { employeeNo: record.employeeNo },
                })
              }
            >
              <EyeOutlined />
            </Button>
          </Tooltip>
          <Tooltip title="Edit Resource">
            <Button type="link" onClick={() => handleEdit(record)}>
              <EditOutlined />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const defaultCheckedList = columns.map((item) => item.key);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);

  const handleColumnSelection = (value) => {
    setCheckedList(value);
  };

  const newColumns = columns.filter((item) => checkedList.includes(item.key));

  const handleResourceTableData = async () => {
    try {
      setLoading(true);
      const {
        page,
        sortField,
        sortOrder,
        filterField,
        search,
        skills,
        projects,
      } = tableParams;
      const response = await resource.getResourceData(
        page,
        sortField,
        sortOrder,
        filterField,
        search,
        skills,
        projects,
        selectTeam,
        employeeId,
        globalSearch
      );
      setResourceTableData(response.data.data);
      setTotalCount(response.data.totalCount);
      setBuId(response.data.BUId);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (buId) {
      const matchedOption = options.find((option) => option.value === buId);
      if (matchedOption) {
        setSelectTeam(matchedOption.value);
      }
    }
  }, [buId]);

  useEffect(() => {
    handleResourceTableData();
  }, [tableParams, selectTeam, globalSearch, isEditModalOpen]);

  const handleTableChange = ({
    pagination,
    filterField,
    search,
    sortField,
    sortOrder,
  }) => {
    const obj = { ...tableParams };

    if (search !== undefined) obj.search = search;
    if (filterField !== undefined) obj.filterField = filterField;
    if (sortField !== undefined) obj.sortField = sortField;
    if (sortOrder !== undefined) obj.sortOrder = sortOrder;
    obj.page = pagination?.current || 1;

    if (skillsData.length > 0 && selectSkill) {
      obj.skills = selectSkill;
    } else {
      delete obj.skills;
    }

    if (projectList.length > 0 && selectProject) {
      obj.projects = selectProject;
    } else {
      delete obj.projects;
    }

    updateURLParams(obj);

    setTableParams(obj);
  };

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    handleGetSkillsData();
    handleProjectList();
  }, []);

  const handleGetSkillsData = async () => {
    try {
      const response = await employee.getSkills();
      const newResponse = response.data.map((skill) => ({
        ...skill,
        value: skill.skillId,
        label: skill.skill,
      }));
      setSkillsData(newResponse);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSkillSelection = (selectedValues) => {
    const selectedNames = skillsData
      .filter((skill) => selectedValues.includes(skill.value))
      .map((skill) => skill.label);

    setSelectSkill(selectedNames);
    updateURLParams({ skills: selectedNames.join(",") });

    setTableParams((prevParams) => ({
      ...prevParams,
      page: 1,
      skills: selectedNames,
    }));
  };

  const handleProjectList = async () => {
    try {
      const response = await employee.getProjectList();
      const newResponse = response.data.map((project) => ({
        ...project,
        value: project.projectId,
        label: project.projectName,
      }));

      const withoutProjectOption = {
        value: "On Bench",
        label: "On Bench",
      };

      setProjectList([withoutProjectOption, ...newResponse]);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleProjectSelection = (selectedValues) => {
    const selectedNames = projectList
      .filter((project) => selectedValues.includes(project.value))
      .map((project) => project.label);

    setProjectSkill(selectedNames);
    updateURLParams({ projects: selectedNames.join(",") });

    setTableParams((prevParams) => ({
      ...prevParams,
      page: 1,
      projects: selectedNames,
    }));
  };

  const filteredOptions =
    buId == "SPAN-BLR-0001" || employeeId == "SPAN-BLR-0001"
      ? options
      : options.filter((option) => option.value === buId);

  const handleSelectTeam = (value) => {
    setTableParams({
      page: 1,
      sortField: null,
      sortOrder: null,
      filterField: null,
      search: "",
      skills: [],
      projects: [],
    });
    setSelectSkill([]);
    setProjectSkill([]);
    setGlobalSearch("");
    setSelectTeam(value);
    updateURLParams({ projects: "", skills: "" });
  };

  const handleResetAll = () => {
    setTableParams({
      page: 1,
      sortField: null,
      sortOrder: null,
      filterField: null,
      search: "",
      skills: [],
      projects: [],
    });
    setCheckedList(defaultCheckedList);
    setSelectSkill([]);
    setProjectSkill([]);
    updateURLParams({
      projects: "",
      skills: "",
      globalSearch: "",
      page: 1,
      sortField: null,
      sortOrder: null,
      filterField: null,
      search: "",
    });
    window.location.reload();
  };

  return (
    <>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span style={{ width: "250px", marginRight: "10px" }}>
          <Search
            placeholder="Search"
            value={globalSearch}
            onChange={(e) => {
              setGlobalSearch(e.target.value);
              updateURLParams({ globalSearch: e.target.value });
            }}
            style={{ width: 200, marginRight: "10px" }}
          />
        </span>

        <div>
          <Dropdown
            overlay={
              <Select
                style={{ width: "140px" }}
                filterOption={filterOption}
                optionFilterProp="children"
                placeholder="Select Team"
                options={filteredOptions}
                value={selectTeam}
                onChange={(value) => handleSelectTeam(value)}
              />
            }
            trigger={["click"]}
          >
            <Button type="text">
              <span style={{ fontSize: "14px", fontWeight: "700" }}>
                View team{" "}
              </span>
              {selectTeam ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </Button>
          </Dropdown>

          <Dropdown
            overlay={
              <Select
                showSearch
                allowClear
                mode="multiple"
                style={{ width: "200px" }}
                filterOption={filterOption}
                optionFilterProp="children"
                placeholder="Select Skills"
                options={skillsData}
                value={skillsData
                  .filter((skill) => selectSkill.includes(skill.label))
                  .map((skill) => skill.value)}
                onChange={handleSkillSelection}
                maxTagCount="responsive"
                tagRender={(props) => {
                  const { label, closable, onClose } = props;
                  return (
                    <Tooltip
                      title={
                        selectSkill.length > 0
                          ? selectSkill.join(", ")
                          : "No skills selected"
                      }
                      placement="top"
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "#f0f0f0",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          marginRight: "4px",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{ marginLeft: "4px", cursor: "pointer" }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    </Tooltip>
                  );
                }}
              />
            }
            trigger={["click"]}
          >
            <Tooltip
              title={
                selectSkill.length > 0
                  ? selectSkill.join(", ")
                  : "No skills selected"
              }
              placement="top"
            >
              <Button type="text">
                <span style={{ fontSize: "14px", fontWeight: "700" }}>
                  Skills{" "}
                </span>
                {selectSkill.length > 0 ? <FilterFilled /> : <FilterOutlined />}
              </Button>
            </Tooltip>
          </Dropdown>

          <Dropdown
            overlay={
              <Select
                showSearch
                allowClear
                mode="multiple"
                style={{ width: "200px" }}
                filterOption={filterOption}
                optionFilterProp="children"
                placeholder="Select Projects"
                options={projectList}
                value={projectList
                  .filter((project) => selectProject.includes(project.label))
                  .map((project) => project.value)}
                onChange={handleProjectSelection}
                maxTagCount="responsive"
                tagRender={(props) => {
                  const { label, closable, onClose } = props;
                  return (
                    <Tooltip
                      title={
                        selectProject.length > 0
                          ? selectProject.join(", ")
                          : "No projects selected"
                      }
                      placement="top"
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "#f0f0f0",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          marginRight: "4px",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{ marginLeft: "4px", cursor: "pointer" }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    </Tooltip>
                  );
                }}
              />
            }
            trigger={["click"]}
          >
            <Tooltip
              title={
                selectProject.length > 0
                  ? selectProject.join(", ")
                  : "No projects selected"
              }
              placement="top"
            >
              <span>
                <Button type="text">
                  <span style={{ fontSize: "14px", fontWeight: "700" }}>
                    Projects{" "}
                  </span>
                  {selectProject.length > 0 ? (
                    <FilterFilled />
                  ) : (
                    <FilterOutlined />
                  )}
                </Button>
              </span>
            </Tooltip>
          </Dropdown>

          <Dropdown
            overlay={
              <Select
                showSearch
                mode="multiple"
                style={{ width: "200px" }}
                placeholder="Select Columns"
                filterOption={filterOption}
                optionFilterProp="children"
                value={checkedList}
                onChange={handleColumnSelection}
                options={columns.map((col) => ({
                  label: col.title,
                  value: col.key,
                  disabled: col.fixed === "left" || col.fixed === "right",
                }))}
                maxTagCount="responsive"
              />
            }
            trigger={["click"]}
          >
            <Button type="text">
              <span style={{ fontSize: "14px", fontWeight: "700" }}>
                Columns{" "}
              </span>
              {checkedList.length < 18 ? <FilterFilled /> : <FilterOutlined />}
            </Button>
          </Dropdown>

          <Button
            type="text"
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color:
                globalSearch ||
                selectSkill.length > 0 ||
                selectProject.length > 0 ||
                checkedList.length < columns.length ||
                tableParams.page !== 1 ||
                tableParams.sortField !== null ||
                tableParams.sortOrder !== null ||
                tableParams.filterField !== null ||
                tableParams.search !== ""
                  ? "#1890ff"
                  : "black",
            }}
            onClick={handleResetAll}
          >
            Reset All
          </Button>
        </div>
      </div>

      <TableComponent
        loading={loading}
        rows={resourceTableData}
        columns={newColumns}
        scrollx={2900}
        scrolly={494}
        totalPages={totalCount}
        pagination={{
          total: totalCount,
          pageSize: 50,
          current: tableParams.page,
        }}
        onChange={handleTableChange}
        summary={true}
        isActivity={true}
        locale={{ emptyText: "No data available for given input" }}
      />

      <ResourceEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        record={editingRecord}
      />
    </>
  );
}

export default ResourceTable;
